import React, { useContext, useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme/theme.js";
import LoginPage from "./pages/auth/LoginPage.js";
import MainLayout from "./layouts/MainLayout.js";
import { LoaderContextProvider } from "./context/LoaderContext.js";
import { UserContextProvider, UserContext } from "./context/UserContext.js";
import { loginRequest } from "./authConfig.js";
import UserService from "./service/userService.js";

// L'URI di redirect registrato in Azure AD punta alla root dell'app, quindi la
// risposta del login AAD atterra sempre qui, indipendentemente dalla route
// visualizzata prima del redirect. Per questo l'elaborazione va fatta a questo
// livello (sopra le Routes) e non nella sola LoginPage.
const hasPendingAuthRedirect = () =>
  /[?#](.*&)?(code|error|id_token|access_token)=/.test(
    window.location.hash + window.location.search
  );

function AppRoutes() {
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const { setUser } = useContext(UserContext);
  const [isProcessingRedirect, setIsProcessingRedirect] = useState(
    hasPendingAuthRedirect()
  );
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None || hasProcessed.current) return;
    hasProcessed.current = true;

    const processRedirect = async () => {
      try {
        const result = await instance.handleRedirectPromise();
        if (result && result.account) {
          instance.setActiveAccount(result.account);

          const tokenResult = await instance.acquireTokenSilent({
            ...loginRequest,
            account: result.account,
          });

          const loginResult = await UserService.login(tokenResult.accessToken);
          if (!loginResult.success) {
            throw new Error(loginResult.message || "Login fallito");
          }

          const date = new Date();
          date.setTime(date.getTime() + 24 * 60 * 60 * 1000);
          document.cookie = `Q8TechAuth=${tokenResult.accessToken}; expires=${date.toUTCString()}; path=/; SameSite=Strict`;

          const userData = await UserService.getUserInfo();
          setUser(userData);

          navigate("/", { replace: true });
        }
      } catch (error) {
        console.error("Errore durante l'elaborazione del redirect di login:", error);
        navigate("/login", { replace: true });
      } finally {
        setIsProcessingRedirect(false);
      }
    };

    processRedirect();
  }, [instance, inProgress, navigate, setUser]);

  if (isProcessingRedirect) {
    return null;
  }

  return (
    <Routes>
      {/* Route per la pagina di login */}
      <Route path="/login" element={<LoginPage />} />

      {/* Route protetta per tutte le altre pagine */}
      <Route path="/*" element={<MainLayout />} />
    </Routes>
  );
}

function App() {
  return (
    <UserContextProvider>
      <LoaderContextProvider>
        <ThemeProvider theme={theme}>
          <Router>
            <AppRoutes />
          </Router>
        </ThemeProvider>
      </LoaderContextProvider>
    </UserContextProvider>
  );
}

export default App;

